import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import service from '../../store/store/baseapiservice';

interface RTIRequestDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentLanguage: string;
}

interface TicketSuccessData {
  ticketNumber: string;
  assignedPersonName: string;
  assignedPersonPhone: string;
}

export default function RTIRequestDrawer({ isOpen, onClose, currentLanguage }: RTIRequestDrawerProps) {
  const [formData, setFormData] = useState({
    rtiRequestType: 'Self', // 'Self' or 'Others'
    name: '',
    mobile: '',
    email: '',
    gender: '', // '1' for Male, '2' for Female
    address: '',
    povertyLine: '', // '1' for Yes, '2' for No
    description: '',
    document: null as File | null
  });
  const [documentPreview, setDocumentPreview] = useState<string | null>(null);
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaId, setCaptchaId] = useState<number | null>(null);
  const [captchaImage, setCaptchaImage] = useState<string>('');
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [ticketSuccessData, setTicketSuccessData] = useState<TicketSuccessData | null>(null);
  const [assignedUsersList, setAssignedUsersList] = useState<Array<{ usr_id: number; usr_nm?: string; fst_nm?: string; mble_ph?: string }>>([]);
  const [selectedAssignedUserId, setSelectedAssignedUserId] = useState<number | null>(null);

  // Fetch assigned users from team employees
  const fetchAssignedUsers = async () => {
    try {
      const postData = {
        tm_id: 874
      };
      const response: any = await service.post('/support/grievances/ticket/team/employees', postData);
      
      if (response && response.status === 200 && Array.isArray(response.data)) {
        setAssignedUsersList(response.data);
        // If only one user, auto-select it
        if (response.data.length === 1) {
          setSelectedAssignedUserId(response.data[0].usr_id);
        } else if (response.data.length > 1) {
          // If multiple users, set first one as default but allow selection
          setSelectedAssignedUserId(response.data[0].usr_id);
        }
      }
    } catch (err: any) {
      console.error('Error fetching assigned users:', err);
      // Set default to 0 if fetch fails
      setSelectedAssignedUserId(0);
    }
  };

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (isOpen) {
      generateCaptcha();
      fetchAssignedUsers();
      // Pre-fill user data if available
      const citizenName = localStorage.getItem('citizenName');
      const citizenMobile = localStorage.getItem('citizenMobile');
      const citizenEmail = localStorage.getItem('citizenEmail');
      const citizenAddress = localStorage.getItem('citizenAddress');
      
      if (citizenName) setFormData(prev => ({ ...prev, name: citizenName }));
      if (citizenMobile) setFormData(prev => ({ ...prev, mobile: citizenMobile }));
      if (citizenEmail) setFormData(prev => ({ ...prev, email: citizenEmail }));
      if (citizenAddress) setFormData(prev => ({ ...prev, address: citizenAddress }));
    } else {
      // Reset form when drawer closes
      setFormData({
        rtiRequestType: 'Self',
        name: '',
        mobile: '',
        email: '',
        gender: '',
        address: '',
        povertyLine: '',
        description: '',
        document: null
      });
      setDocumentPreview(null);
      setCaptchaInput('');
      setSubmitError(null);
      setAssignedUsersList([]);
      setSelectedAssignedUserId(null);
    }
  }, [isOpen]);


  const generateCaptcha = async () => {
    setIsLoadingCaptcha(true);
    try {
      const response: any = await service.get('/auth2/ss/login/capcha');
      
      if (response && response.status === 200 && response.data) {
        setCaptchaId(response.data.cptch_id);
        setCaptchaImage(response.data.cptch_img_src_tx);
        setCaptchaInput('');
      } else {
        console.error('Failed to fetch captcha:', response?.message);
      }
    } catch (err: any) {
      console.error('Error fetching captcha:', err);
    } finally {
      setIsLoadingCaptcha(false);
    }
  };


  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, document: file });
      const reader = new FileReader();
      reader.onload = (event) => {
        setDocumentPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.rtiRequestType) {
      setSubmitError(currentLanguage === 'en' ? 'Please select RTI Request type' : 'దయచేసి RTI అభ్యర్థన రకాన్ని ఎంచుకోండి');
      return;
    }
    if (!formData.name.trim()) {
      setSubmitError(currentLanguage === 'en' ? 'Please enter name' : 'దయచేసి పేరు నమోదు చేయండి');
      return;
    }
    if (!formData.mobile.trim()) {
      setSubmitError(currentLanguage === 'en' ? 'Please enter mobile number' : 'దయచేసి మొబైల్ నంబర్ నమోదు చేయండి');
      return;
    }
    if (!formData.gender) {
      setSubmitError(currentLanguage === 'en' ? 'Please select gender' : 'దయచేసి లింగం ఎంచుకోండి');
      return;
    }
    if (!formData.address.trim()) {
      setSubmitError(currentLanguage === 'en' ? 'Please enter address' : 'దయచేసి చిరునామా నమోదు చేయండి');
      return;
    }
    if (!formData.povertyLine) {
      setSubmitError(currentLanguage === 'en' ? 'Please select poverty line status' : 'దయచేసి పేదరిక రేఖ స్థితిని ఎంచుకోండి');
      return;
    }
    if (!formData.description.trim()) {
      setSubmitError(currentLanguage === 'en' ? 'Please enter RTI request description' : 'దయచేసి RTI అభ్యర్థన వివరణను నమోదు చేయండి');
      return;
    }
    if (formData.description.trim().length > 3000) {
      setSubmitError(currentLanguage === 'en' ? 'Description should not exceed 3000 characters' : 'వివరణ 3000 అక్షరాలను మించకూడదు');
      return;
    }
    if (!captchaInput.trim()) {
      setSubmitError(currentLanguage === 'en' ? 'Please enter captcha' : 'దయచేసి క్యాప్చా నమోదు చేయండి');
      return;
    }
    if (!captchaId) {
      setSubmitError(currentLanguage === 'en' ? 'Captcha not loaded. Please refresh and try again.' : 'క్యాప్చా లోడ్ కాలేదు. దయచేసి రిఫ్రెష్ చేసి మళ్లీ ప్రయత్నించండి.');
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Convert document to base64 attachments array if provided
      // RTI handler only accepts: base64, fl_name, fl_type
      const attachments: Array<{
        base64: string;
        fl_name: string;
        fl_type: string;
      }> = [];

      if (formData.document) {
        const document = formData.document;
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve, reject) => {
          reader.onload = (e) => {
            const base64 = e.target?.result as string;
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(document);
        });
        const documentBase64 = await base64Promise;

        attachments.push({
          base64: documentBase64,
          fl_name: document.name,
          fl_type: document.type
        });
      }

      // Prepare request data with correct field names for RTI endpoint
      // Note: captchaID and captcha are not included as they're not in the RTI validation schema
      const isSelf = formData.rtiRequestType === 'Self';
      const requestData = {
        data: {
          ...(isSelf ? {} : {
            ownr_nm: formData.name,
            ownr_ph: formData.mobile
          }),
          emaiId: formData.email || '',
          Address: formData.address,
          lng_dscn_tx: formData.description,
          atchmnts: attachments,
          tckt_type_id: 3,
          tckt_ctgry_id: 30,
          tkt_srce_id: 11,
          sprt_usr_id: selectedAssignedUserId || (assignedUsersList.length > 0 ? assignedUsersList[0].usr_id : 0),
          pvty_id: parseInt(formData.povertyLine, 10), // 1 for Yes, 2 for No
          gender: parseInt(formData.gender, 10), // 1 for Male, 2 for Female
          updtctzen: isSelf
        }
      };

      const response: any = await service.post('/public/web/e-request/rti/ticket', requestData);

      if (response && response.status === 200) {
        // Extract ticket details from response
        // RTI endpoint returns [results, tckt_id] array
        const responseData = Array.isArray(response.data) ? response.data : response.data?.data || response.data;
        const ticketId = Array.isArray(responseData) && responseData.length > 1 
          ? responseData[1] 
          : responseData?.tckt_id || responseData?.tckt_num || responseData?.[0]?.tckt_id || 'N/A';
        
        // Try to get assigned user info from various possible response structures
        const results = Array.isArray(responseData) ? responseData[0] : responseData;
        let assignedPersonName = '';
        let assignedPersonPhone = '';
        
        // First, try to get from the assignedUsersList we fetched (most reliable)
        if (selectedAssignedUserId && assignedUsersList.length > 0) {
          const assignedUser = assignedUsersList.find(user => user.usr_id === selectedAssignedUserId);
          if (assignedUser) {
            assignedPersonName = assignedUser.fst_nm || assignedUser.usr_nm || '';
            assignedPersonPhone = assignedUser.mble_ph || '';
          }
        }
        
        // If not found, check multiple possible response structures for assigned user info
        if (!assignedPersonName && results) {
          // Check if results is an array with user info
          if (Array.isArray(results) && results.length > 0) {
            const userInfo = results.find(item => item.usr_id || item.fst_nm || item.usr_nm);
            if (userInfo) {
              assignedPersonName = userInfo.fst_nm || userInfo.usr_nm || userInfo.name || '';
              assignedPersonPhone = userInfo.mble_ph || userInfo.phone || userInfo.usr_ph || '';
            }
          } else if (typeof results === 'object') {
            // Check for assigned user in various possible fields
            const assignedUser = results.asgn_usrid || results.asgn_usr_id || results.assigned_user || results.user;
            
            if (Array.isArray(assignedUser) && assignedUser.length > 0) {
              assignedPersonName = assignedUser[0].fst_nm || assignedUser[0].usr_nm || assignedUser[0].name || '';
              assignedPersonPhone = assignedUser[0].mble_ph || assignedUser[0].phone || assignedUser[0].usr_ph || '';
            } else if (assignedUser && typeof assignedUser === 'object') {
              assignedPersonName = assignedUser.fst_nm || assignedUser.usr_nm || assignedUser.name || '';
              assignedPersonPhone = assignedUser.mble_ph || assignedUser.phone || assignedUser.usr_ph || '';
            } else if (results.fst_nm || results.usr_nm) {
              // Direct user info in results
              assignedPersonName = results.fst_nm || results.usr_nm || results.name || '';
              assignedPersonPhone = results.mble_ph || results.phone || results.usr_ph || '';
            }
          }
        }

        // Set success data and show popup
        setTicketSuccessData({
          ticketNumber: String(ticketId),
          assignedPersonName: assignedPersonName.trim(),
          assignedPersonPhone: assignedPersonPhone.trim()
        });
        setShowSuccessPopup(true);
      } else {
        throw new Error(response?.message || 'Failed to submit RTI request');
      }
    } catch (err: any) {
      console.error('Error submitting RTI request:', err);
      setSubmitError(err.message || (currentLanguage === 'en'
        ? 'Failed to submit RTI request. Please try again.'
        : 'RTI అభ్యర్థనను సమర్పించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
      generateCaptcha();
      setCaptchaInput('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);
    setTicketSuccessData(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[10000]"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[10000] flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-white font-semibold text-lg uppercase">
                {currentLanguage === 'en' ? 'RIGHT TO INFORMATION(RTI)' : 'సమాచార హక్కు(RTI)'}
              </h2>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Mandatory Fields Notice */}
              <div className="mb-4 text-red-600 text-sm font-medium">
                {currentLanguage === 'en' ? 'Fields marked with * are mandatory.' : 'తో గుర్తించబడిన ఫీల్డ్‌లు తప్పనిసరి.'}
              </div>

              {submitError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* RTI Request Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'RTI Request?' : 'RTI అభ్యర్థన?'} <span className="text-red-600">*</span>
                  </label>
                  <select
                    value={formData.rtiRequestType}
                    onChange={(e) => setFormData({ ...formData, rtiRequestType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    required
                  >
                    <option value="Self">{currentLanguage === 'en' ? 'Self' : 'స్వీయ'}</option>
                    <option value="Others">{currentLanguage === 'en' ? 'Others' : 'ఇతరులు'}</option>
                  </select>
                </div>

                {/* Personal Details Section */}
                <div className="mt-6">
                  <h3 className="text-orange-600 font-semibold text-base mb-3">
                    {currentLanguage === 'en' ? 'Personal Details of RTI Applicant:-' : 'RTI అభ్యర్థి వ్యక్తిగత వివరాలు:-'}
                  </h3>

                  {/* Name */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {currentLanguage === 'en' ? 'Name' : 'పేరు'} <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      required
                    />
                  </div>

                  {/* Mobile Number */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {currentLanguage === 'en' ? 'Mobile Number' : 'మొబైల్ నంబర్'} <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      required
                    />
                  </div>

                  {/* Gender */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {currentLanguage === 'en' ? 'Gender' : 'లింగం'} <span className="text-red-600">*</span>
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="gender"
                          value="1"
                          checked={formData.gender === '1'}
                          onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                          className="mr-2"
                          required
                        />
                        <span className="text-sm">{currentLanguage === 'en' ? 'Male' : 'పురుషుడు'}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="gender"
                          value="2"
                          checked={formData.gender === '2'}
                          onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                          className="mr-2"
                          required
                        />
                        <span className="text-sm">{currentLanguage === 'en' ? 'Female' : 'స్త్రీ'}</span>
                      </label>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {currentLanguage === 'en' ? 'Email-ID' : 'ఇమెయిల్-ID'}
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>

                  {/* Address */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {currentLanguage === 'en' ? 'Enter Address' : 'చిరునామా నమోదు చేయండి'} <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={3}
                      placeholder={currentLanguage === 'en' ? 'Complete Address' : 'పూర్తి చిరునామా'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y text-sm"
                      required
                    />
                  </div>
                </div>

                {/* Request Details Section */}
                <div className="mt-6">
                  <h3 className="text-orange-600 font-semibold text-base mb-3">
                    {currentLanguage === 'en' ? 'Request Details:-' : 'అభ్యర్థన వివరాలు:-'}
                  </h3>

                  {/* Poverty Line */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {currentLanguage === 'en' ? 'Is the Applicant Below Poverty Line ?' : 'అభ్యర్థి పేదరిక రేఖకు దిగువన ఉన్నారా?'} <span className="text-red-600">*</span>
                    </label>
                    <select
                      value={formData.povertyLine}
                      onChange={(e) => setFormData({ ...formData, povertyLine: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      required
                    >
                      <option value="">{currentLanguage === 'en' ? 'Select' : 'ఎంచుకోండి'}</option>
                      <option value="1">{currentLanguage === 'en' ? 'Yes' : 'అవును'}</option>
                      <option value="2">{currentLanguage === 'en' ? 'No' : 'కాదు'}</option>
                    </select>
                  </div>

                  {/* RTI Request Description */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {currentLanguage === 'en' ? 'Describe your RTI Request' : 'మీ RTI అభ్యర్థనను వివరించండి'} <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={6}
                      maxLength={3000}
                      placeholder={currentLanguage === 'en' ? 'Enter Text for RTI Request application upto 3000 characters' : 'RTI అభ్యర్థన దరఖాస్తు కోసం 3000 అక్షరాల వరకు టెక్స్ట్ నమోదు చేయండి'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y text-sm"
                      required
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {formData.description.length}/3000 {currentLanguage === 'en' ? 'characters' : 'అక్షరాలు'}
                    </div>
                  </div>
                </div>

                {/* Upload Document */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Upload Document' : 'డాక్యుమెంట్ అప్‌లోడ్ చేయండి'}
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors bg-gray-50">
                    {documentPreview ? (
                      <div className="space-y-2">
                        <img
                          src={documentPreview}
                          alt="Document preview"
                          className="max-w-full h-32 object-contain mx-auto rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, document: null });
                            setDocumentPreview(null);
                          }}
                          className="text-red-600 hover:text-red-700 text-xs font-medium"
                        >
                          {currentLanguage === 'en' ? 'Remove Document' : 'డాక్యుమెంట్ తీసివేయండి'}
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <div className="flex flex-col items-center">
                          <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                          <p className="text-gray-600 text-xs">
                            {currentLanguage === 'en' ? 'Click to upload document' : 'డాక్యుమెంట్ అప్‌లోడ్ చేయడానికి క్లిక్ చేయండి'}
                          </p>
                        </div>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={handleDocumentChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Captcha */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Enter Captcha' : 'క్యాప్చా నమోదు చేయండి'} <span className="text-red-600">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 border border-gray-300 rounded-lg p-3 flex items-center justify-center min-h-[50px]">
                      {isLoadingCaptcha ? (
                        <div className="text-gray-400">
                          <i className="ri-loader-4-line text-xl animate-spin"></i>
                        </div>
                      ) : captchaImage ? (
                        <img 
                          src={captchaImage} 
                          alt="Captcha" 
                          className="max-h-12"
                        />
                      ) : (
                        <div className="text-gray-400 text-xs">
                          {currentLanguage === 'en' ? 'Captcha not loaded' : 'క్యాప్చా లోడ్ కాలేదు'}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={generateCaptcha}
                      disabled={isLoadingCaptcha}
                      className="w-10 h-10 bg-purple-600 hover:bg-purple-700 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={currentLanguage === 'en' ? 'Refresh captcha' : 'క్యాప్చా రిఫ్రెష్ చేయండి'}
                    >
                      <i className="ri-refresh-line text-lg"></i>
                    </button>
                  </div>
                  <input
                    type="text"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    placeholder={currentLanguage === 'en' ? 'Enter the code from the above image' : 'పై చిత్రం నుండి కోడ్ నమోదు చేయండి'}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    required
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        {currentLanguage === 'en' ? 'Submitting...' : 'సమర్పిస్తోంది...'}
                      </span>
                    ) : (
                      currentLanguage === 'en' ? 'SUBMIT' : 'సమర్పించండి'
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>

          {/* Success Popup Modal */}
          <AnimatePresence>
            {showSuccessPopup && ticketSuccessData && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-[10001]"
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                  className="fixed inset-0 flex items-center justify-center z-[10001] pointer-events-none"
                >
                  <div className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full mx-4 pointer-events-auto">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-3xl"></i>
                      </div>
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-green-600 mb-2">
                          {currentLanguage === 'en' ? 'Ticket Created Successfully!' : 'టికెట్ విజయవంతంగా సృష్టించబడింది!'}
                        </h3>
                        <p className="text-gray-700 text-base mb-3">
                          {currentLanguage === 'en'
                            ? `Ticket No: ${ticketSuccessData.ticketNumber}`
                            : `టికెట్ నంబర్: ${ticketSuccessData.ticketNumber}`}
                        </p>
                        {(ticketSuccessData.assignedPersonName || ticketSuccessData.assignedPersonPhone) ? (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600 mb-2">
                              {currentLanguage === 'en' ? 'Assigned To:' : 'కేటాయించబడింది:'}
                            </p>
                            {ticketSuccessData.assignedPersonName && (
                              <p className="text-base font-semibold text-gray-800 mb-1">
                                {ticketSuccessData.assignedPersonName}
                              </p>
                            )}
                            {ticketSuccessData.assignedPersonPhone && (
                              <p className="text-sm text-blue-600 flex items-center">
                                <i className="ri-phone-line mr-1"></i>
                                {ticketSuccessData.assignedPersonPhone}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                              {currentLanguage === 'en' 
                                ? 'Assigned person details will be updated soon.' 
                                : 'కేటాయించిన వ్యక్తి వివరాలు త్వరలో నవీకరించబడతాయి.'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-6">
                      <motion.button
                        onClick={handleSuccessPopupClose}
                        className="w-full px-4 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {currentLanguage === 'en' ? 'OK' : 'సరే'}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}

